import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Camera, ImageIcon, Trash2, X, ChevronLeft, ChevronRight, AlertTriangle, Upload,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Button, Spinner } from "@/components/ui";

// =============================================================================
// AssetPhotoGallery — reusable photo gallery for any asset kind.
// Uploads to the 'asset-documents' Supabase Storage bucket, tracks rows in
// public.asset_photos. Signed URLs for display since the bucket is private.
// T-051. Drop into any asset detail screen:
//   <AssetPhotoGallery assetKind="rigging" assetId={item.rigging_id} canEdit={canEdit} />
// =============================================================================

export type AssetKind = "vehicle" | "power_tool" | "hand_tool" | "rigging";

const BUCKET = "asset-documents";
const MAX_PHOTO_BYTES = 10 * 1024 * 1024;                                       // 10 MB
const ACCEPTED_MIME = ["image/jpeg", "image/png", "image/webp", "image/heic"];
const SIGNED_URL_TTL = 60 * 60;                                                 // 1 hour

interface PhotoRow {
  photo_id:       string;
  storage_path:   string;
  filename:       string | null;
  caption:        string | null;
  mime_type:      string | null;
  size_bytes:     number | null;
  uploaded_at:    string;
  uploaded_by_employee_id: string | null;
}

interface PhotoWithUrl extends PhotoRow {
  signedUrl: string | null;
}

interface Props {
  assetKind: AssetKind;
  assetId:   string;
  canEdit:   boolean;
}

// -----------------------------------------------------------------------------

export function AssetPhotoGallery({ assetKind, assetId, canEdit }: Props) {
  const qc = useQueryClient();
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch rows -------------------------------------------------------------
  const { data: photos = [], isLoading } = useQuery({
    queryKey: ["asset_photos", assetKind, assetId],
    enabled: !!assetId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("asset_photos")
        .select("photo_id, storage_path, filename, caption, mime_type, size_bytes, uploaded_at, uploaded_by_employee_id")
        .eq("asset_kind", assetKind)
        .eq("asset_id", assetId)
        .is("doc_kind", null)          // mig 510 — documents (rego, safety cert…) live on the Documents card, not here
        .order("uploaded_at", { ascending: false });
      if (error) throw error;
      const rows = (data ?? []) as PhotoRow[];

      // Sign each photo — batched createSignedUrls() is one round trip.
      if (rows.length === 0) return [] as PhotoWithUrl[];
      const { data: signed, error: sErr } = await supabase
        .storage
        .from(BUCKET)
        .createSignedUrls(rows.map(r => r.storage_path), SIGNED_URL_TTL);
      if (sErr) throw sErr;

      const urlByPath = new Map(
        (signed ?? []).map(s => [s.path ?? "", s.signedUrl ?? null]),
      );
      return rows.map(r => ({ ...r, signedUrl: urlByPath.get(r.storage_path) ?? null }));
    },
  });

  // Upload -----------------------------------------------------------------
  const uploadMut = useMutation({
    mutationFn: async (file: File) => {
      if (!ACCEPTED_MIME.includes(file.type)) {
        throw new Error("Unsupported image type. JPEG / PNG / WEBP / HEIC only.");
      }
      if (file.size > MAX_PHOTO_BYTES) {
        throw new Error(`Photo too large (${(file.size / 1024 / 1024).toFixed(1)} MB, max 10 MB).`);
      }
      const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      const rand  = Math.random().toString(36).slice(2, 8);
      const path  = `${assetKind}/${assetId}/${stamp}-${rand}.${ext}`;

      const { error: upErr } = await supabase.storage.from(BUCKET).upload(path, file, {
        contentType: file.type,
        cacheControl: "3600",
        upsert: false,
      });
      if (upErr) throw upErr;

      const { error: rowErr } = await supabase.from("asset_photos").insert({
        asset_kind: assetKind,
        asset_id:   assetId,
        storage_path: path,
        filename:   file.name,
        mime_type:  file.type,
        size_bytes: file.size,
      });
      if (rowErr) {
        // Row insert failed — try to roll back the upload.
        await supabase.storage.from(BUCKET).remove([path]).catch(() => {});
        throw rowErr;
      }
    },
    onSuccess: () => {
      setUploadErr(null);
      qc.invalidateQueries({ queryKey: ["asset_photos", assetKind, assetId] });
    },
    onError: (e: unknown) => {
      setUploadErr(e instanceof Error ? e.message : "Upload failed");
    },
  });

  const deleteMut = useMutation({
    mutationFn: async (photo: PhotoRow) => {
      // Storage first — if the row delete later fails, we can re-clean manually.
      await supabase.storage.from(BUCKET).remove([photo.storage_path]).catch(() => {});
      const { error } = await supabase.from("asset_photos").delete().eq("photo_id", photo.photo_id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["asset_photos", assetKind, assetId] });
      setLightboxIdx(null);
    },
    onError: (e: unknown) => alert(e instanceof Error ? e.message : "Delete failed"),
  });

  const onFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    // Sequential uploads to keep error messaging simple.
    (async () => {
      for (const f of Array.from(files)) {
        try {
          await uploadMut.mutateAsync(f);
        } catch { /* onError surfaces message */ break; }
      }
      if (fileInputRef.current) fileInputRef.current.value = "";
    })();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[13px] font-medium text-text flex items-center gap-2">
          <ImageIcon size={15} className="text-accent" />
          Photos
          <span className="text-[11px] text-text-muted font-normal">({photos.length})</span>
        </div>
        {canEdit && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_MIME.join(",")}
              capture="environment"
              multiple
              onChange={onFilePicked}
              className="hidden"
            />
            <Button
              variant="secondary"
              size="sm"
              icon={Camera}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadMut.isPending}
            >
              {uploadMut.isPending ? "Uploading…" : "Add photo"}
            </Button>
          </>
        )}
      </div>

      {uploadErr && (
        <div className="mb-2 p-2 rounded bg-red-900/20 border border-red-900/40 text-[12px] text-red-300 flex items-center gap-2">
          <AlertTriangle size={12} />
          {uploadErr}
        </div>
      )}

      {isLoading ? (
        <div className="py-6 flex justify-center"><Spinner /></div>
      ) : photos.length === 0 ? (
        <div className="py-8 flex flex-col items-center justify-center gap-2 border border-dashed border-line rounded-lg text-text-muted">
          <ImageIcon size={22} />
          <div className="text-[12px]">
            {canEdit
              ? <>No photos yet — click <span className="text-accent">Add photo</span> to upload condition shots, ID plates, defects.</>
              : "No photos yet."}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {photos.map((p, idx) => (
            <button
              key={p.photo_id}
              onClick={() => setLightboxIdx(idx)}
              className="aspect-square bg-ink-raised border border-line rounded-md overflow-hidden hover:border-accent transition-colors group relative"
              type="button"
            >
              {p.signedUrl ? (
                <img
                  src={p.signedUrl}
                  alt={p.caption ?? p.filename ?? "Asset photo"}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-text-muted">
                  <ImageIcon size={20} />
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {lightboxIdx != null && photos[lightboxIdx] && (
        <Lightbox
          photos={photos}
          index={lightboxIdx}
          onChange={setLightboxIdx}
          onClose={() => setLightboxIdx(null)}
          onDelete={canEdit ? (photo) => {
            if (window.confirm(`Delete this photo? This can't be undone.`)) {
              deleteMut.mutate(photo);
            }
          } : undefined}
          deletePending={deleteMut.isPending}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------

function Lightbox({
  photos, index, onChange, onClose, onDelete, deletePending,
}: {
  photos: PhotoWithUrl[];
  index: number;
  onChange: (i: number) => void;
  onClose: () => void;
  onDelete?: (p: PhotoRow) => void;
  deletePending: boolean;
}) {
  const photo = photos[index];

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft" && index > 0) onChange(index - 1);
      else if (e.key === "ArrowRight" && index < photos.length - 1) onChange(index + 1);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [index, photos.length, onChange, onClose]);

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex flex-col items-center justify-center p-4" onClick={onClose}>
      <div className="absolute right-4 flex items-center gap-2" style={{ top: "calc(var(--safe-top, 0px) + 1rem)" }} onClick={e => e.stopPropagation()}>
        {onDelete && photo && (
          <Button
            variant="danger"
            size="sm"
            icon={Trash2}
            onClick={() => onDelete(photo)}
            disabled={deletePending}
          >
            {deletePending ? "Deleting…" : "Delete"}
          </Button>
        )}
        <button
          onClick={onClose}
          className="p-2 rounded text-white/70 hover:text-white hover:bg-white/10"
          type="button"
          aria-label="Close"
        >
          <X size={20} />
        </button>
      </div>

      <div className="max-w-[90vw] max-h-[85vh]" onClick={e => e.stopPropagation()}>
        {photo?.signedUrl ? (
          <img
            src={photo.signedUrl}
            alt={photo.caption ?? photo.filename ?? "Asset photo"}
            className="max-w-[90vw] max-h-[85vh] object-contain"
          />
        ) : (
          <div className="text-white/60 p-10 flex items-center gap-2">
            <Upload size={18} />
            Preview unavailable.
          </div>
        )}
      </div>

      <div className="mt-3 text-white/80 text-[12px] font-mono" onClick={e => e.stopPropagation()}>
        {photo?.filename ?? "photo"} · {index + 1} of {photos.length}
        {photo?.uploaded_at && (
          <span className="ml-2 text-white/50">· {new Date(photo.uploaded_at).toLocaleString("en-AU")}</span>
        )}
      </div>

      {index > 0 && (
        <button
          onClick={e => { e.stopPropagation(); onChange(index - 1); }}
          className="absolute left-3 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 text-white hover:bg-white/20"
          type="button"
          aria-label="Previous"
        >
          <ChevronLeft size={20} />
        </button>
      )}
      {index < photos.length - 1 && (
        <button
          onClick={e => { e.stopPropagation(); onChange(index + 1); }}
          className="absolute right-3 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 text-white hover:bg-white/20"
          type="button"
          aria-label="Next"
        >
          <ChevronRight size={20} />
        </button>
      )}
    </div>
  );
}
